import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Check, Plus, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { FileAttacher, toolboxFileHref, type AttachedFile } from "@/components/toolbox/file-attacher";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { TOOLBOX_PURPOSES, purposeText, todayLocal, type ToolboxMeeting } from "@/lib/toolbox";
import { useSession } from "@/lib/auth-client";
import { useGoBack } from "@/hooks/use-go-back";
import { useToolboxScope } from "@/hooks/use-toolbox-scope";
import { GrantAccessLink } from "@/components/toolbox/give-access-dialog";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/toolbox/new")({
  validateSearch: (s: Record<string, unknown>): { edit?: string; assignment?: string } => ({
    ...(typeof s.edit === "string" ? { edit: s.edit } : {}),
    ...(typeof s.assignment === "string" ? { assignment: s.assignment } : {}),
  }),
  component: NewToolboxPage,
});

interface Signer {
  key: string;
  id?: string;
  userId: string | null;
  name: string;
  company: string | null;
  source: "crew" | "sub" | "manual";
}
interface Person { userId: string; name: string; role?: string | null; company?: string }
interface Candidates { crew: Person[]; divisionCrew: Person[]; subs: Person[] }

const STEPS = ["Meeting", "Discussion", "Attendees", "Review"] as const;

function NewToolboxPage() {
  const { edit, assignment } = Route.useSearch();
  const goBack = useGoBack(edit ? { to: "/toolbox/$meetingId", params: { meetingId: edit } } : { to: "/toolbox" });
  const navigate = useNavigate();
  const { data: session } = useSession();
  const myId = session?.user?.id ?? "";

  // Super admins and division admins can build a toolbox for no project, one or several, and with no attendees: it is then
  // saved in the prepared talks (no project) or assigned to those projects' foremen. With attendees it is held now (one project).
  const { data: adminInfo } = useQuery({
    queryKey: ["toolbox", "can-assign"],
    queryFn: () => apiGet<{ canAssign?: boolean }>("/api/toolbox?status=collecting"),
    staleTime: 60_000,
  });
  const [pickedProjects, setPickedProjects] = useState<string[]>([]);
  const [projectQuery, setProjectQuery] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignNote, setAssignNote] = useState("");

  const [step, setStep] = useState(0);
  // The furthest step reached: every step up to it can be jumped to, and what was typed in later steps stays
  const [reached, setReached] = useState(0);
  useEffect(() => {
    setReached((r) => Math.max(r, step));
  }, [step]);
  const [projectId, setProjectId] = useState("");
  // Names typed in when the site or person is not in the app
  const [projectOther, setProjectOther] = useState("");
  const [foremanOther, setForemanOther] = useState("");
  const [conductedOther, setConductedOther] = useState("");
  const [purposeOther, setPurposeOther] = useState("");
  const [date, setDate] = useState(todayLocal());
  const [subject, setSubject] = useState("");
  const [purpose, setPurpose] = useState<string[]>([]);
  const [moreDetails, setMoreDetails] = useState("");
  const [foremanUserId, setForemanUserId] = useState("");
  const [extraForemen, setExtraForemen] = useState<{ userId?: string; name: string }[]>([]);
  const [extraConductors, setExtraConductors] = useState<{ userId?: string; name: string }[]>([]);
  const [conductedByUserId, setConductedByUserId] = useState("");
  const [topics, setTopics] = useState("");
  const [hazards, setHazards] = useState("");
  const [signers, setSigners] = useState<Signer[]>([]);
  const [manualName, setManualName] = useState("");
  const [manualCompany, setManualCompany] = useState("");
  const [otherUser, setOtherUser] = useState("");
  const [loadedEdit, setLoadedEdit] = useState(false);

  const scope = useToolboxScope();
  const { data: assignableData } = useQuery({
    queryKey: ["delegations", "projects"],
    queryFn: () => apiGet<{ data: { id: string; name: string; client: string | null }[] }>("/api/delegations/projects"),
    staleTime: 60_000,
  });
  const { data: projectsData } = useQuery({
    queryKey: ["projects", "active", "all-for-toolbox"],
    queryFn: () => apiGet<{ data: { id: string; name: string; client: string | null }[] }>("/api/projects?status=active&limit=100"),
  });
  const { data: talksData } = useQuery({
    queryKey: ["toolbox", "talks", ""],
    queryFn: () =>
      apiGet<{ data: { id: string; title: string; purpose: string[]; moreDetails: string | null; topicsDiscussed: string | null; hazardComments: string | null }[] }>("/api/toolbox/talks"),
    enabled: !edit,
  });
  // Started from a toolbox an admin assigned: the talk, the site, the due date and the note come with it
  const { data: assignedData } = useQuery({
    queryKey: ["toolbox", "assigned", assignment],
    queryFn: () =>
      apiGet<{ data: { targetId: string; title: string; purpose: string[]; moreDetails: string | null; topicsDiscussed: string | null; hazardComments: string | null; dueDate: string | null; note: string | null; assignedByName: string | null; projectId: string; projectName: string | null; meetingId: string | null } }>(`/api/toolbox/assigned/${assignment}`),
    enabled: !!assignment && !edit,
  });
  const assigned = assignedData?.data;
  const [assignedApplied, setAssignedApplied] = useState(false);
  useEffect(() => {
    if (!assigned || assignedApplied) return;
    setAssignedApplied(true);
    setProjectId(assigned.projectId);
    setSubject(assigned.title);
    setPurpose(assigned.purpose);
    setMoreDetails(assigned.moreDetails ?? "");
    setTopics(assigned.topicsDiscussed ?? "");
    setHazards(assigned.hazardComments ?? "");
  }, [assigned, assignedApplied]);

  // Held toolboxes can be the starting point too (everything but the people and signatures is copied)
  const { data: heldData } = useQuery({
    queryKey: ["toolbox", "library", "", "", "", ""],
    queryFn: () => apiGet<{ data: { id: string; subject: string; date: string; projectName: string | null; isPaper?: boolean }[] }>("/api/toolbox/library"),
    enabled: !edit,
    staleTime: 30_000,
  });
  const { data: usersData } = useQuery({
    queryKey: ["toolbox", "people"],
    // The whole Paris team (any division, any role), not only the people the account could see elsewhere
    queryFn: () => apiGet<{ data: { id: string; name: string; hint: string }[] }>("/api/toolbox/people"),
    staleTime: 60_000,
  });
  const { data: cand, isFetching: candLoading } = useQuery({
    queryKey: ["toolbox", "candidates", projectId],
    queryFn: () => apiGet<{ data: Candidates }>(`/api/toolbox/candidates?projectId=${projectId}`),
    enabled: !!projectId,
  });

  const [attachments, setAttachments] = useState<AttachedFile[]>([]);

  const multi = !!adminInfo?.canAssign && !edit && !assigned;
  const assignable = assignableData?.data ?? [];
  // With one project picked the meeting can be held now; with none or several there is nothing to hold, it is prepared or assigned
  useEffect(() => {
    if (!multi) return;
    setProjectOther("");
    setProjectId(pickedProjects.length === 1 ? pickedProjects[0] : "");
  }, [multi, pickedProjects]);
  const mode: "meeting" | "assign" | "prepared" = !multi || signers.length > 0 ? "meeting" : pickedProjects.length > 0 ? "assign" : "prepared";

  // Editing: load the saved meeting once
  const { data: existing } = useQuery({
    queryKey: ["toolbox", "meeting", edit],
    queryFn: () => apiGet<{ data: ToolboxMeeting }>(`/api/toolbox/${edit}`),
    enabled: !!edit,
  });
  useEffect(() => {
    const m = existing?.data;
    if (!m || loadedEdit) return;
    setLoadedEdit(true);
    setProjectId(m.projectId ?? "");
    setProjectOther(m.projectOther ?? "");
    setForemanOther(m.foremanOther ?? "");
    setConductedOther(m.conductedByOther ?? "");
    setPurposeOther(m.purposeOther ?? "");
    setDate(m.date);
    setSubject(m.subject);
    setPurpose(m.purpose);
    setMoreDetails(m.moreDetails ?? "");
    setForemanUserId(m.foremanUserId ?? "");
    setExtraForemen(m.extraForemen ?? []);
    setExtraConductors(m.extraConductors ?? []);
    setConductedByUserId(m.conductedByOther ? "" : m.conductedByUserId);
    setTopics(m.topicsDiscussed ?? "");
    setHazards(m.hazardComments ?? "");
    setSigners(m.signers.map((s) => ({ key: s.id, id: s.id, userId: s.userId, name: s.name, company: s.company, source: s.source })));
    setAttachments((m.attachments ?? []).filter((a) => a.kind !== "paper").map((a) => ({ fileId: a.fileId, fileName: a.fileName, isImage: a.kind === "photo", href: a.kind === "photo" ? toolboxFileHref(a.shareToken) : "" })));
  }, [existing, loadedEdit]);

  // New meeting: the person writing it up is the default for both names
  useEffect(() => {
    if (!edit && myId) {
      setForemanUserId((v) => v || myId);
      setConductedByUserId((v) => v || myId);
    }
  }, [edit, myId]);

  const talks = talksData?.data ?? [];
  const held = (heldData?.data ?? []).filter((h) => !h.isPaper);
  async function applyStart(key: string) {
    const [kind, id] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];
    if (kind === "talk") {
      const t = talks.find((x) => x.id === id);
      if (!t) return;
      setSubject(t.title);
      setPurpose(t.purpose);
      setMoreDetails(t.moreDetails ?? "");
      setTopics(t.topicsDiscussed ?? "");
      setHazards(t.hazardComments ?? "");
      toast.success(`Filled in from "${t.title}"`);
      return;
    }
    try {
      const m = (await apiGet<{ data: ToolboxMeeting }>(`/api/toolbox/${id}`)).data;
      setSubject(m.subject);
      setPurpose(m.purpose);
      setMoreDetails(m.moreDetails ?? "");
      setTopics(m.topicsDiscussed ?? "");
      setHazards(m.hazardComments ?? "");
      toast.success(`Filled in from "${m.subject}"`);
    } catch {
      toast.error("Could not open that toolbox");
    }
  }
  const projects = scope.projectsFor(projectsData?.data ?? []);
  const users = usersData?.data ?? [];
  const chosenIds = useMemo(() => new Set(signers.map((s) => s.userId).filter(Boolean) as string[]), [signers]);

  const has = (userId: string) => chosenIds.has(userId);
  function toggle(p: Person, source: "crew" | "sub") {
    setSigners((cur) =>
      cur.some((s) => s.userId === p.userId)
        ? cur.filter((s) => s.userId !== p.userId)
        : [...cur, { key: p.userId, userId: p.userId, name: p.name, company: p.company ?? null, source }]
    );
  }
  function setAll(list: Person[], source: "crew" | "sub", on: boolean) {
    setSigners((cur) => {
      const rest = cur.filter((s) => !list.some((p) => p.userId === s.userId));
      return on ? [...rest, ...list.map((p) => ({ key: p.userId, userId: p.userId, name: p.name, company: p.company ?? null, source }))] : rest;
    });
  }
  function addOtherUser(id: string) {
    const u = users.find((x) => x.id === id);
    if (!u || has(u.id)) return;
    setSigners((cur) => [...cur, { key: u.id, userId: u.id, name: u.name, company: null, source: "crew" }]);
    setOtherUser("");
  }
  function addManual() {
    const name = manualName.trim();
    if (!name) return;
    setSigners((cur) => [...cur, { key: `m-${Date.now()}`, userId: null, name, company: manualCompany.trim() || null, source: "manual" }]);
    setManualName("");
    setManualCompany("");
  }

  const save = useMutation({
    mutationFn: async (mode: "draft" | "sign" | "auto") => {
      const ask = mode !== "draft";
      const body = {
        projectId,
        projectOther: projectId ? "" : projectOther,
        foremanOther: foremanUserId ? "" : foremanOther,
        conductedByOther: conductedByUserId ? "" : conductedOther,
        purposeOther: purpose.includes("other") ? purposeOther : "",
        date,
        subject,
        purpose,
        moreDetails,
        topicsDiscussed: topics,
        hazardComments: hazards,
        foremanUserId: foremanUserId || null,
        conductedByUserId: conductedByUserId || undefined,
        extraForemen,
        extraConductors,
        signers: signers.map((s) => ({ id: s.id, userId: s.userId, name: s.name, company: s.company, source: s.source })),
        attachmentFileIds: attachments.map((a) => a.fileId),
        ...(assigned && !edit ? { assignmentTargetId: assigned.targetId } : {}),
      };
      let id = edit;
      if (edit) await apiPatch(`/api/toolbox/${edit}`, body);
      else id = (await apiPost<{ data: { id: string } }>("/api/toolbox", body)).data.id;
      if (ask) await apiPost(`/api/toolbox/${id}/assign`, { autoSend: mode === "auto" });
      return { id: id!, ask, mode };
    },
    onSuccess: ({ id, ask, mode }) => {
      toast.success(ask ? (mode === "auto" ? "Saved. The attendees were asked to sign, and it is emailed when the last one signs." : "Saved. The attendees were asked to sign.") : "Saved as a draft");
      navigate({ to: "/toolbox/$meetingId", params: { meetingId: id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // No attendees: save the toolbox as a prepared talk (no project) or assign it to the picked projects' foremen
  const publish = useMutation({
    mutationFn: async () => {
      const content = { title: subject.trim(), purpose: purpose.filter((k) => k !== "other"), moreDetails, topicsDiscussed: topics, hazardComments: hazards };
      if (mode === "prepared") {
        await apiPost("/api/toolbox/talks", content);
        return { kind: "prepared" as const };
      }
      // It is kept in the prepared talks as well, then handed to the picked projects' foremen
      const talk = await apiPost<{ data: { id: string } }>("/api/toolbox/talks", content);
      const r = await apiPost<{ data: { foremen: number; sites: number; noForeman: string[] } }>("/api/toolbox/assignments", { sourceKind: "talk", sourceId: talk.data.id, projectIds: pickedProjects, dueDate: dueDate || undefined, note: assignNote });
      return { kind: "assign" as const, ...r.data };
    },
    onSuccess: (r) => {
      if (r.kind === "prepared") {
        toast.success("Saved in the prepared talks");
        navigate({ to: "/toolbox/library", search: { tab: "talks" } });
        return;
      }
      toast.success(`Assigned to ${r.foremen} foreman${r.foremen !== 1 ? "men" : ""} on ${r.sites} site${r.sites !== 1 ? "s" : ""}. They were told.`);
      if (r.noForeman.length > 0) toast.warning(`No foreman on: ${r.noForeman.join(", ")}`);
      navigate({ to: "/toolbox/assignments" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const problem = (s: number): string | null => {
    if (s === 0) {
      if (!multi && !projectId && !projectOther.trim()) return "Choose the project";
      if (!subject.trim()) return "Write the subject of the meeting";
    }
    if (s === 2) {
      if (multi && signers.length > 0 && pickedProjects.length !== 1) return "To hold the meeting with attendees, pick exactly one project. Or remove the attendees to assign it or save it as a prepared talk.";
      if (!multi && signers.length === 0) return "Add the people who attended";
    }
    return null;
  };
  // Leave without saving. A new meeting that has something typed asks first; an edit goes back to the meeting.
  const dirty = !edit && !!(projectId || pickedProjects.length > 0 || projectOther.trim() || subject.trim() || moreDetails.trim() || topics.trim() || hazards.trim() || signers.length > 0);
  const cancel = () => {
    if (dirty && !window.confirm("Cancel this toolbox meeting? Nothing has been saved, so what you typed will be lost.")) return;
    if (edit) navigate({ to: "/toolbox/$meetingId", params: { meetingId: edit } });
    else navigate({ to: "/toolbox" });
  };
  // No project picked on the first step: say what that means before going on
  const [noProjectWarning, setNoProjectWarning] = useState(false);
  const next = () => {
    const p = problem(step);
    if (p) return toast.error(p);
    if (step === 0 && multi && pickedProjects.length === 0) {
      setNoProjectWarning(true);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const finish = (mode: "draft" | "sign" | "auto") => {
    for (const s of [0, 2]) {
      const p = problem(s);
      if (p) { setStep(s); return toast.error(p); }
    }
    save.mutate(mode);
  };

  const projectName = projects.find((p) => p.id === projectId)?.name ?? projectOther;
  const userName = (id: string) => users.find((u) => u.id === id)?.name ?? (id === myId ? session?.user?.name ?? "" : "");
  const peopleOptions = users.map((u) => ({ value: u.id, label: u.name, hint: u.hint }));
  const hasCrewList = (cand?.data.crew.length ?? 0) > 0;
  const crewList = hasCrewList ? cand!.data.crew : cand?.data.divisionCrew ?? [];

  const box = "w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm";

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-24">
      <div className="flex items-center gap-2 pt-2">
        <button type="button" onClick={goBack} className="rounded p-1 hover:bg-muted" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="min-w-0 flex-1 text-xl font-bold text-text-primary">{edit ? "Edit toolbox meeting" : "New toolbox meeting"}</h1>
        <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={cancel}>Cancel</Button>
      </div>

      <ol className="flex items-center gap-1 text-xs">
        {STEPS.map((label, i) => (
          <li key={label} className="flex flex-1 items-center gap-1">
            <button
              type="button"
              onClick={() => {
                if (i <= step) return setStep(i);
                // Forward: only to a step already reached, and only if the steps before it are filled in
                if (i > reached) return next();
                for (let k = step; k < i; k++) {
                  const p = problem(k);
                  if (p) { setStep(k); return toast.error(p); }
                }
                setStep(i);
              }}
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                i === step ? "bg-paris-red text-white" : i <= reached ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"
              )}
              aria-current={i === step ? "step" : undefined}
            >
              {i !== step && i <= reached ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </button>
            <span className={cn("hidden truncate sm:inline", i === step ? "font-semibold" : "text-muted-foreground")}>{label}</span>
            {i < STEPS.length - 1 && <span className="h-px flex-1 bg-border" />}
          </li>
        ))}
      </ol>
      <p className="text-sm font-semibold sm:hidden">Step {step + 1}: {STEPS[step]}</p>

      {step === 0 && (
        <div className="space-y-4">
          {assigned && (
            <div className="space-y-1 rounded-lg border-2 border-red-600 bg-red-50 p-3 text-sm dark:bg-red-950/30">
              <p className="font-semibold text-red-700 dark:text-red-300">Assigned to you{assigned.assignedByName ? ` by ${assigned.assignedByName}` : ""}</p>
              <p>{assigned.dueDate ? `Due ${formatDateOnly(assigned.dueDate)}` : "No due date"}{assigned.projectName ? ` · ${assigned.projectName}` : ""}</p>
              {assigned.note && <p className="whitespace-pre-line text-muted-foreground">{assigned.note}</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="tb-subject">Subject *</Label>
            <p className="text-xs text-muted-foreground">Write your own toolbox subject here, or choose a prepared talk or a held toolbox in the box below and the subject (and the rest of the form) is filled in for you.</p>
            <Input id="tb-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Example: Working at heights, ladders and fall arrest" className={"h-11 text-base md:text-sm placeholder:italic placeholder:text-muted-foreground/50"} />
          </div>
          {!edit && !assigned && talks.length + held.length > 0 && (
            <div className="space-y-1.5 rounded-lg border border-dashed p-3">
              <Label htmlFor="tb-talk">Or start from a prepared talk or a held toolbox (optional)</Label>
              <SearchableSelect
                id="tb-talk"
                value=""
                onChange={(v) => void applyStart(v)}
                allLabel="Choose a prepared talk or held toolbox..."
                allValue=""
                searchPlaceholder="Type a title..."
                options={[
                  ...talks.map((t) => ({ value: `talk:${t.id}`, label: t.title, hint: "Prepared talk" })),
                  ...held.map((h) => ({ value: `held:${h.id}`, label: h.subject, hint: ["Held", h.projectName, formatDateOnly(h.date)].filter(Boolean).join(" · ") })),
                ]}
              />
              <p className="text-xs text-muted-foreground">Choosing one fills in the subject, purpose, details, topics and hazards. You can still change anything.</p>
            </div>
          )}
          {multi ? (
            <div className="space-y-1.5">
              <Label htmlFor="tb-project-search">Projects / sites (optional)</Label>
              <p className="text-xs text-muted-foreground">Pick one project to hold this meeting now with attendees. Pick several and add no attendees to assign this toolbox to those projects: their foremen are told. Pick none and add no attendees to keep it in the prepared talks.</p>
              <Input id="tb-project-search" value={projectQuery} onChange={(e) => setProjectQuery(e.target.value)} placeholder="Search projects..." className="h-10 text-base md:text-sm" />
              <div className="max-h-44 space-y-1 overflow-y-auto">
                {assignable.filter((p) => !projectQuery || p.name.toLowerCase().includes(projectQuery.toLowerCase())).map((p) => (
                  <label key={p.id} htmlFor={`tb-pp-${p.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                    <input id={`tb-pp-${p.id}`} type="checkbox" className="h-4 w-4" checked={pickedProjects.includes(p.id)} onChange={() => setPickedProjects((cur) => (cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id]))} />
                    <span className="min-w-0 flex-1 truncate">{p.name}{p.client ? <span className="ml-2 text-xs text-muted-foreground">{p.client}</span> : null}</span>
                  </label>
                ))}
                {assignable.length === 0 && <p className="text-xs text-muted-foreground">No active projects to pick.</p>}
              </div>
              <p className="text-xs text-muted-foreground">{pickedProjects.length === 0 ? "No project picked." : `${pickedProjects.length} project${pickedProjects.length !== 1 ? "s" : ""} picked.`}{!!adminInfo?.canAssign ? " A division admin sees only their own division's projects." : ""}</p>
            </div>
          ) : (
          <div className="space-y-1.5">
            <Label htmlFor="tb-project">Project / site *</Label>
            <SearchableSelect id="tb-project" value={projectId} customText={projectOther} onChange={(v) => { setProjectId(v); setProjectOther(""); if (!edit) setSigners([]); }} onCustom={scope.limited ? undefined : (t) => { setProjectId(""); setProjectOther(t); if (!edit) setSigners([]); }} allLabel="Choose the project" allValue="" searchPlaceholder="Type a project name, or a site that is not listed..." options={projects.map((p) => ({ value: p.id, label: p.name, hint: p.client }))} />
          </div>
          )}
          {projectId && <GrantAccessLink kind="toolbox" projectId={projectId} />}
          <div className="space-y-1.5">
            <Label htmlFor="tb-date">Date *</Label>
            <input id="tb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={box} />
          </div>
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Purpose of the meeting</legend>
            {TOOLBOX_PURPOSES.map((p) => (
              <label key={p.key} htmlFor={`tb-p-${p.key}`} className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm">
                <input
                  id={`tb-p-${p.key}`}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={purpose.includes(p.key)}
                  onChange={() => setPurpose((cur) => (cur.includes(p.key) ? cur.filter((k) => k !== p.key) : [...cur, p.key]))}
                />
                {p.label}
              </label>
            ))}
            {purpose.includes("other") && (
              <Input id="tb-purpose-other" aria-label="Other purpose" value={purposeOther} onChange={(e) => setPurposeOther(e.target.value)} placeholder="Write the purpose of the meeting" className="h-11 text-base md:text-sm" />
            )}
          </fieldset>
          <div className="space-y-1.5">
            <Label htmlFor="tb-more">More details</Label>
            <p className="text-xs text-muted-foreground">Why this meeting is being held: what changed, or which incident or task led to it.</p>
            <Textarea id="tb-more" value={moreDetails} onChange={(e) => setMoreDetails(e.target.value)} rows={3} className="placeholder:italic placeholder:text-muted-foreground/50" placeholder="Example: A new ladder procedure starts this week after a near miss on the second floor." />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tb-foreman">Supervisor / foreman (optional)</Label>
            <SearchableSelect id="tb-foreman" value={foremanUserId} customText={foremanOther} onChange={(v) => { setForemanUserId(v); setForemanOther(""); }} onCustom={(t) => { setForemanUserId(""); setForemanOther(t); }} allLabel="Choose..." allValue="" searchPlaceholder="Type a name..." options={peopleOptions} />
            <ExtraPeople label="Add another supervisor / foreman" list={extraForemen} onChange={setExtraForemen} options={peopleOptions} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tb-by">Toolbox conducted by (optional)</Label>
            <SearchableSelect id="tb-by" value={conductedByUserId} customText={conductedOther} onChange={(v) => { setConductedByUserId(v); setConductedOther(""); }} onCustom={(t) => { setConductedByUserId(""); setConductedOther(t); }} allLabel="Choose..." allValue="" searchPlaceholder="Type a name..." options={peopleOptions} />
            <ExtraPeople label="Add another person who conducted it" list={extraConductors} onChange={setExtraConductors} options={peopleOptions} />
            <p className="text-xs text-muted-foreground">Both names can be left blank while you prepare a toolbox. When a meeting is held with no conductor chosen, it shows you.</p>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tb-topics">Topics discussed</Label>
            <p className="text-xs text-muted-foreground">The points you actually went over with the crew in this meeting. Write one per line.</p>
            <Textarea id="tb-topics" value={topics} onChange={(e) => setTopics(e.target.value)} rows={8} className="placeholder:italic placeholder:text-muted-foreground/50" placeholder={"Example (this is only a sample, write your own):\n1. Three points of contact on ladders\n2. Harness inspection before each use"} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tb-haz">Hazard identification / comments / questions</Label>
            <p className="text-xs text-muted-foreground">Hazards found or raised on site, plus any comments or questions from the crew.</p>
            <Textarea id="tb-haz" value={hazards} onChange={(e) => setHazards(e.target.value)} rows={6} className="placeholder:italic placeholder:text-muted-foreground/50" placeholder="Example: Wet floor near the east stairwell. A worker asked whether the harness needs re-inspecting after a fall." />
          </div>
          <div className="space-y-1.5">
            <Label>Upload a document/photo or take a photo</Label>
            <p className="text-xs text-muted-foreground">Photos are shown in the PDF. Documents are listed in the PDF by name, with a link to a read-only copy that anyone who gets the PDF can open.</p>
            <FileAttacher items={attachments} onChange={setAttachments} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          {multi && signers.length === 0 && (
            <div className="rounded-lg border border-dashed p-3 text-sm">
              <p className="font-medium">No attendees needed</p>
              <p className="text-xs text-muted-foreground">
                {pickedProjects.length > 0
                  ? `With no attendees this toolbox is assigned to the ${pickedProjects.length} project${pickedProjects.length !== 1 ? "s" : ""} you picked, and their foremen are told.`
                  : "With no attendees and no project this toolbox is kept in the prepared talks."}{" "}
                Add attendees only to hold the meeting now (with exactly one project).
              </p>
            </div>
          )}
          <p className="text-sm text-muted-foreground">Pick everyone who attended at <strong>{projectName || "the project"}</strong>. They are asked to sign, on their own phone or on yours.</p>

          {!(multi && !projectId) && (<>
          <PersonList
            title={hasCrewList ? "Project crew" : "Project divisions' crew"}
            people={crewList}
            loading={candLoading}
            has={has}
            onToggle={(p) => toggle(p, "crew")}
            onAll={(on) => setAll(crewList, "crew", on)}
            empty="No crew is set up on this project yet. Add people below."
          />
          {(cand?.data.subs.length ?? 0) > 0 && (
            <PersonList
              title="Subcontractors on this project"
              people={cand!.data.subs}
              loading={false}
              has={has}
              onToggle={(p) => toggle(p, "sub")}
              onAll={(on) => setAll(cand!.data.subs, "sub", on)}
              empty=""
              showCompany
            />
          )}
          </>)}

          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-sm font-medium">Add someone else</p>
            <SearchableSelect value={otherUser} onChange={addOtherUser} allLabel="Search a Paris Mechanical person..." allValue="" searchPlaceholder="Type a name..." options={peopleOptions.filter((o) => !chosenIds.has(o.value))} />
            <p className="text-xs text-muted-foreground">Not in the app? Type their name:</p>
            <div className="flex gap-2">
              <Input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Full name" className="h-10 flex-1 text-base md:text-sm" aria-label="Full name" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addManual())} />
              <Input value={manualCompany} onChange={(e) => setManualCompany(e.target.value)} placeholder="Company" className="h-10 w-32 text-base md:text-sm" aria-label="Company" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addManual())} />
              <Button type="button" variant="outline" onClick={addManual} disabled={!manualName.trim()} aria-label="Add person"><UserPlus className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">Attendance list ({signers.length})</p>
            {signers.length === 0 && <p className="text-xs text-muted-foreground">Nobody added yet.</p>}
            {signers.map((s) => (
              <div key={s.key} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {s.name}
                  {s.company && <span className="ml-1 text-xs text-muted-foreground">({s.company})</span>}
                  {!s.userId && <span className="ml-1 text-[11px] text-amber-600">signs on your device</span>}
                </span>
                <button type="button" className="p-1 text-muted-foreground hover:text-destructive" aria-label={`Remove ${s.name}`} onClick={() => setSigners((cur) => cur.filter((x) => x.key !== s.key))}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="space-y-1.5 rounded-lg border p-3 text-sm">
            <Row label="Project" value={multi ? (pickedProjects.length > 0 ? pickedProjects.map((id) => assignable.find((p) => p.id === id)?.name ?? "").filter(Boolean).join("; ") : "None (prepared talks)") : projectName} onEdit={() => setStep(0)} />
            <Row label="Date" value={formatDateOnly(date)} onEdit={() => setStep(0)} />
            <Row label="Subject" value={subject} onEdit={() => setStep(0)} />
            <Row label="Purpose" value={purposeText(purpose, purposeOther) || "-"} onEdit={() => setStep(0)} />
            <Row label="Supervisor / foreman" value={[userName(foremanUserId) || foremanOther, ...extraForemen.map((p) => p.name)].filter(Boolean).join(", ") || "-"} onEdit={() => setStep(0)} />
            <Row label="Conducted by" value={[userName(conductedByUserId) || conductedOther, ...extraConductors.map((p) => p.name)].filter(Boolean).join(", ") || "-"} onEdit={() => setStep(0)} />
            <Row label="Discussion" value={topics.trim() || hazards.trim() ? "Topics and hazards written" : "-"} onEdit={() => setStep(1)} />
            <Row label="Attachments" value={`${attachments.length}`} onEdit={() => setStep(1)} />
            <Row label="Attendees" value={`${signers.length}`} onEdit={() => setStep(2)} />
          </div>
          {mode !== "meeting" && (
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-sm font-medium">{mode === "assign" ? `This toolbox will be assigned to ${pickedProjects.length} project${pickedProjects.length !== 1 ? "s" : ""}` : "This toolbox will be saved in the prepared talks"}</p>
              <p className="text-xs text-muted-foreground">
                {mode === "assign"
                  ? "The foremen of those projects are told, with the due date and note. They hold the meeting and collect the signatures. It is also kept in the prepared talks."
                  : "Nothing is held now. Anyone who runs toolboxes can use it later from the library."}
              </p>
              {attachments.length > 0 && <p className="text-xs text-amber-700">Photos and documents are only kept with a held meeting, so these {attachments.length} are not included.</p>}
              {mode === "assign" && (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="tb-due">Due date (optional)</Label>
                    <input id="tb-due" type="date" min={todayLocal()} value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={box} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tb-assign-note">Note for the foremen (optional)</Label>
                    <Textarea id="tb-assign-note" value={assignNote} onChange={(e) => setAssignNote(e.target.value)} rows={2} placeholder="Example: Hold this one before the concrete pour." className="placeholder:italic placeholder:text-muted-foreground/50" />
                  </div>
                </div>
              )}
              <Button className="w-full" disabled={publish.isPending} onClick={() => { for (const k of [0, 2]) { const p = problem(k); if (p) { setStep(k); return toast.error(p); } } publish.mutate(); }}>
                {publish.isPending ? "Saving..." : mode === "assign" ? `Assign to ${pickedProjects.length} project${pickedProjects.length !== 1 ? "s" : ""} and tell the foremen` : "Save in the prepared talks"}
              </Button>
            </div>
          )}
          {mode === "meeting" && (<>
          <p className="text-sm text-muted-foreground">
            Attendees who have the app get a message to sign, and a text reminder every 5 minutes if they have not. After 3 reminders the foreman gets a text. People without the app can sign on your phone from the meeting page.
          </p>
          <div className="flex flex-col gap-2">
            <Button className="w-full" disabled={save.isPending} onClick={() => finish("auto")}>
              {save.isPending ? "Saving..." : "Send for signature and email it automatically"}
            </Button>
            <p className="-mt-1 text-xs text-muted-foreground">The signed record is emailed to the project contacts and Paris team recipients as soon as the last person signs, and you are told.</p>
            <Button className="w-full" variant="outline" disabled={save.isPending} onClick={() => finish("sign")}>
              {edit ? "Remind attendees to sign (I will send it myself)" : "Send for signature only (I will send it myself)"}
            </Button>
            <Button className="w-full" variant="ghost" disabled={save.isPending} onClick={() => finish("draft")}>Save as draft</Button>
          </div>
          </>)}
        </div>
      )}

      <Dialog open={noProjectWarning} onOpenChange={setNoProjectWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>No project picked</DialogTitle>
          <DialogDescription>
            You have not chosen a project. If you do not choose one, this toolbox will be saved in the prepared talks when you finish (unless you add attendees on the next steps, which needs exactly one project).
          </DialogDescription>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => { setNoProjectWarning(false); setTimeout(() => document.getElementById("tb-project-search")?.focus(), 50); }}>Pick a project</Button>
            <Button type="button" onClick={() => { setNoProjectWarning(false); setStep((cur) => Math.min(STEPS.length - 1, cur + 1)); }}>Continue without a project</Button>
          </div>
        </DialogContent>
      </Dialog>

      {step < 3 && (
        <div className="fixed inset-x-0 bottom-14 z-30 border-t bg-background/95 px-4 py-3 backdrop-blur md:bottom-0 md:left-60">
          <div className="mx-auto flex max-w-lg gap-2">
            <Button type="button" variant="ghost" className="text-muted-foreground" onClick={cancel}>Cancel</Button>
            {step > 0 && <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>Back</Button>}
            {reached === 3 && (
              <Button type="button" variant="outline" onClick={() => { const p = [0, 2].map((k) => problem(k)).find(Boolean); if (p) return toast.error(p); setStep(3); }}>
                Back to review
              </Button>
            )}
            <Button type="button" className="flex-1" onClick={next}>Next</Button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="fixed inset-x-0 bottom-14 z-30 border-t bg-background/95 px-4 py-3 backdrop-blur md:bottom-0 md:left-60">
          <div className="mx-auto flex max-w-lg gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(2)}>Back</Button>
            <Button type="button" variant="ghost" className="text-muted-foreground" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** More people for a field: shown as chips, with a picker (or a typed name) to add one more. */
function ExtraPeople({
  label,
  list,
  onChange,
  options,
}: {
  label: string;
  list: { userId?: string; name: string }[];
  onChange: (next: { userId?: string; name: string }[]) => void;
  options: { value: string; label: string; hint?: string | null }[];
}) {
  // A new key after each pick empties the picker again
  const [round, setRound] = useState(0);
  const taken = new Set(list.flatMap((p) => (p.userId ? [p.userId] : [])));
  return (
    <div className="space-y-1.5 pt-1">
      {list.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {list.map((p, i) => (
            <li key={`${p.userId ?? p.name}-${i}`} className="flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-3 pr-1 text-sm">
              <span>{p.name}</span>
              <button type="button" className="rounded-full p-1 text-muted-foreground hover:text-destructive" aria-label={`Remove ${p.name}`} onClick={() => onChange(list.filter((_, j) => j !== i))}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <SearchableSelect
        key={round}
        value=""
        customText=""
        allLabel={`+ ${label}`}
        allValue=""
        searchPlaceholder="Type a name..."
        options={options.filter((o) => !taken.has(o.value))}
        onChange={(v) => {
          const o = options.find((x) => x.value === v);
          if (o) onChange([...list, { userId: o.value, name: o.label }]);
          setRound((r) => r + 1);
        }}
        onCustom={(t) => {
          const name = t.trim();
          if (name) onChange([...list, { name }]);
          setRound((r) => r + 1);
        }}
      />
    </div>
  );
}

function Row({ label, value, onEdit }: { label: string; value: string; onEdit?: () => void }) {
  return (
    <p className="flex gap-2">
      <span className="w-36 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words">{value}</span>
      {onEdit && (
        <button type="button" className="shrink-0 text-xs font-medium text-paris-red-text underline underline-offset-2" onClick={onEdit} aria-label={`Edit ${label}`}>
          Edit
        </button>
      )}
    </p>
  );
}

function PersonList({
  title, people, loading, has, onToggle, onAll, empty, showCompany,
}: {
  title: string;
  people: Person[];
  loading: boolean;
  has: (id: string) => boolean;
  onToggle: (p: Person) => void;
  onAll: (on: boolean) => void;
  empty: string;
  showCompany?: boolean;
}) {
  const [q, setQ] = useState("");
  const shown = people.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const allOn = people.length > 0 && people.every((p) => has(p.userId));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{title} <span className="font-normal text-muted-foreground">({people.length})</span></p>
        {people.length > 0 && (
          <button type="button" className="text-xs font-medium text-paris-red-text underline underline-offset-2" onClick={() => onAll(!allOn)}>
            {allOn ? "Clear all" : "Select all"}
          </button>
        )}
      </div>
      {people.length > 8 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="h-9 text-base md:text-sm" aria-label={`Search ${title}`} />}
      {loading && <p className="text-xs text-muted-foreground">Loading...</p>}
      {!loading && people.length === 0 && empty && <p className="text-xs text-muted-foreground">{empty}</p>}
      <div className="max-h-72 divide-y overflow-y-auto rounded-lg border">
        {shown.map((p) => (
          <label key={p.userId} htmlFor={`tb-c-${p.userId}`} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted/40">
            <input id={`tb-c-${p.userId}`} type="checkbox" className="h-4 w-4" checked={has(p.userId)} onChange={() => onToggle(p)} />
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
            {showCompany && p.company && <span className="truncate text-xs text-muted-foreground">{p.company}</span>}
            {p.role && <span className="text-[11px] text-muted-foreground">{p.role}</span>}
          </label>
        ))}
      </div>
    </div>
  );
}
